/**
 * Thermal Printer Service for Vacu-Track
 * Uses QZ Tray for direct thermal printing via ESC/POS commands
 * 
 * Setup Instructions:
 * 1. Download QZ Tray from: https://qz.io/download/
 * 2. Install QZ Tray on the kiosk PC
 * 3. Run QZ Tray (it will start automatically with Windows)
 * 4. The first time you print, you'll need to allow the certificate
 */
import { logger } from '../utils/logger';


class ThermalPrinterService {
  constructor() {
    this.qz = null;
    this.isConnected = false;
    this.printerName = null;
    this.paperWidth = 80; // mm
  }

  /**
   * Initialize QZ Tray connection
   */
  async init() {
    if (typeof window.qz === 'undefined') {
      logger.warn('QZ Tray not loaded. Loading from CDN...');
      await this.loadQZScript();
    }
    
    this.qz = window.qz;
    
    if (!this.qz) {
      throw new Error('QZ Tray no está disponible. Por favor instale QZ Tray desde https://qz.io/download/');
    }

    return true;
  }

  /**
   * Load QZ Tray script from CDN
   */
  loadQZScript() {
    return new Promise((resolve, reject) => {
      if (window.qz) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/qz-tray@2.2.4/qz-tray.min.js';
      script.onload = () => {
        // Also load RSVP for QZ Tray promises
        const rsvpScript = document.createElement('script');
        rsvpScript.src = 'https://cdn.jsdelivr.net/npm/rsvp@4.8.5/dist/rsvp.min.js';
        rsvpScript.onload = resolve;
        rsvpScript.onerror = reject;
        document.head.appendChild(rsvpScript);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Connect to QZ Tray
   */
  async connect() {
    try {
      await this.init();
      
      if (!this.qz.websocket.isActive()) {
        await this.qz.websocket.connect();
      }
      
      this.isConnected = true;
      logger.log('Connected to QZ Tray');
      return true;
    } catch (error) {
      console.error('Error connecting to QZ Tray:', error);
      this.isConnected = false;
      throw error;
    }
  }

  /**
   * Disconnect from QZ Tray
   */
  async disconnect() {
    if (this.qz && this.qz.websocket.isActive()) {
      await this.qz.websocket.disconnect();
    }
    this.isConnected = false;
  }

  /**
   * Get list of available printers
   */
  async getPrinters() {
    await this.connect();
    const printers = await this.qz.printers.find();
    return printers;
  }

  /**
   * Set the printer to use
   */
  setPrinter(printerName) {
    this.printerName = printerName;
  }

  /**
   * Find default printer (usually contains "POS", "Thermal", "Receipt", or "80mm")
   */
  async findDefaultPrinter() {
    const printers = await this.getPrinters();
    
    // Look for common thermal printer names
    const thermalKeywords = ['pos', 'thermal', 'receipt', '80mm', '58mm', 'epson', 'star', 'bixolon', 'citizen'];
    
    for (const printer of printers) {
      const lowerName = printer.toLowerCase();
      if (thermalKeywords.some(keyword => lowerName.includes(keyword))) {
        this.printerName = printer;
        return printer;
      }
    }
    
    // Return first printer if no thermal found
    if (printers.length > 0) {
      this.printerName = printers[0];
      return printers[0];
    }
    
    return null;
  }

  /**
   * Generate ESC/POS commands for a ticket
   */
  generateTicketCommands(turno, config = {}) {
    const {
      titulo = 'VACU-TRACK',
      subtitulo = 'by ExtremeBits',
      mensaje = 'Por favor espere a ser llamado',
      footer = 'Gracias por su preferencia'
    } = config;

    const fecha = new Date().toLocaleDateString('es-CL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const hora = new Date().toLocaleTimeString('es-CL', {
      hour: '2-digit',
      minute: '2-digit'
    });

    // ESC/POS Commands
    const ESC = '\x1B';
    const GS = '\x1D';
    const commands = [];

    // Initialize printer
    commands.push(ESC + '@'); // Initialize
    
    // Center alignment
    commands.push(ESC + 'a' + '\x01');
    
    // Bold on, double height
    commands.push(ESC + 'E' + '\x01'); // Bold on
    commands.push(GS + '!' + '\x11'); // Double width and height
    
    // Title
    commands.push(titulo + '\n');
    
    // Normal size for subtitle
    commands.push(GS + '!' + '\x00'); // Normal size
    commands.push(ESC + 'E' + '\x00'); // Bold off
    commands.push(subtitulo + '\n');
    
    // Line separator
    commands.push('--------------------------------\n');
    
    // "SU TURNO ES" text
    commands.push(ESC + 'E' + '\x01'); // Bold on
    commands.push('SU TURNO ES\n\n');
    
    // Large number - Quadruple size
    commands.push(GS + '!' + '\x33'); // 4x width and height
    commands.push(ESC + 'E' + '\x01'); // Bold on
    commands.push(turno.numero_formateado + '\n\n');
    
    // Reset to normal
    commands.push(GS + '!' + '\x00');
    commands.push(ESC + 'E' + '\x00');
    
    // Line separator
    commands.push('--------------------------------\n');
    
    // Date and time
    commands.push(fecha + '\n');
    commands.push(hora + '\n\n');
    
    // Message
    commands.push(mensaje + '\n');
    commands.push(footer + '\n\n');
    
    // Cut paper (partial cut)
    commands.push(GS + 'V' + '\x01');
    
    return commands;
  }

  /**
   * Print a ticket
   */
  async printTicket(turno, config = {}) {
    try {
      await this.connect();
      
      if (!this.printerName) {
        await this.findDefaultPrinter();
      }
      
      if (!this.printerName) {
        throw new Error('No se encontró impresora térmica');
      }

      const commands = this.generateTicketCommands(turno, config);
      
      const printConfig = this.qz.configs.create(this.printerName, {
        encoding: 'UTF-8'
      });
      
      const data = [{
        type: 'raw',
        format: 'command',
        data: commands.join(''),
        options: { language: 'ESCPOS' }
      }];

      await this.qz.print(printConfig, data);
      
      logger.log('Ticket printed successfully');
      return true;
    } catch (error) {
      console.error('Error printing ticket:', error);
      throw error;
    }
  }

  /**
   * Print test ticket
   */
  async printTestTicket() {
    const testTurno = {
      numero_formateado: 'T001',
      numero: 1
    };
    
    return this.printTicket(testTurno, {
      titulo: 'PRUEBA DE IMPRESIÓN',
      subtitulo: 'Vacu-Track',
      mensaje: 'Si ve este ticket correctamente',
      footer: 'la impresora está configurada!'
    });
  }

  /**
   * Check if QZ Tray is available
   */
  async isAvailable() {
    try {
      await this.init();
      await this.connect();
      return true;
    } catch {
      return false;
    }
  }
}

// Singleton instance
const thermalPrinter = new ThermalPrinterService();

export default thermalPrinter;
export { ThermalPrinterService };
